import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { getCustomerStateTransitions } from '../../customer-states/api/customer-states.api';
import type { CustomerState } from '../../customer-states/types/customer-states.types';
import { useChangeCustomerState } from '../hooks/useChangeCustomerState';
import { toast } from 'react-toastify';

interface CustomerStateSelectorProps {
  customerId: string;
  currentStateId: string;
  allStates: CustomerState[];
  onSuccess: (newState: CustomerState) => void;
}

export const CustomerStateSelector: React.FC<CustomerStateSelectorProps> = ({
                                                                              customerId,
                                                                              currentStateId,
                                                                              allStates,
                                                                              onSuccess
                                                                            }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [allowedTransitionIds, setAllowedTransitionIds] = useState<string[]>([]);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [selectedValue, setSelectedValue] = useState(currentStateId);

  const { mutate, isLoading } = useChangeCustomerState();

  useEffect(() => {
    setSelectedValue(currentStateId);
  }, [currentStateId]);

  // Fetch allowed transitions for the current state
  useEffect(() => {
    let isMounted = true;

    const fetchTransitions = async () => {
      setIsFetchingData(true);
      try {
        const transitions = await getCustomerStateTransitions(currentStateId);

        if (isMounted) {
          setAllowedTransitionIds(transitions || []);
        }
      } catch (error) {
        console.error("Error fetching transitions:", error);
      } finally {
        if (isMounted) setIsFetchingData(false);
      }
    };

    if (currentStateId) {
      fetchTransitions();
    }

    return () => { isMounted = false; };
  }, [currentStateId]);

  const handleSelectState = async (newStateId: string) => {
    if (newStateId === currentStateId) return;

    // UX Pesimista: actualizamos visualmente de inmediato
    setSelectedValue(newStateId);

    try {
      await mutate(customerId, newStateId);

      const newStateObj = allStates.find(s => s.id === newStateId);
      if (newStateObj) {
        toast.success('Estado actualizado exitosamente');
        onSuccess(newStateObj);
      }
    } catch (error: any) {
      // Revertimos visualmente en caso de error
      setSelectedValue(currentStateId);

      const errorMessage = error?.response?.data?.message || error?.message || 'Error al cambiar el estado';
      toast.error(errorMessage);
    }
  };

  // Helper function para los colores (igual que en tu filtro)
  const getStateColor = (type?: string): string => {
    switch (type) {
      case 'INITIAL': return '#00a884';
      case 'FINAL': return '#ef5350';
      case 'TRANSIENT': return '#ff9800';
      default: return '#667781';
    }
  };

  // Buscamos el objeto del estado a mostrar (basado en selectedValue para que la UX optimista funcione)
  const displayStateObj = allStates.find(s => s.id === selectedValue) || allStates.find(s => s.id === currentStateId);

  // Estados a los que podemos transicionar
  const allowedStates = allStates.filter(
      s => allowedTransitionIds.includes(s.id) && s.id !== selectedValue
  );

  // Determinamos el color dinámico del borde actual
  const currentBorderColor = displayStateObj ? getStateColor(displayStateObj.type) : '#dee2e6';

  return (
      <Dropdown
          isOpen={isOpen}
          toggle={() => setIsOpen(!isOpen)}
          className="w-auto position-relative"
      >
        <DropdownToggle
            tag="div"
            caret={false}
            disabled={isLoading || isFetchingData}
            className="rounded px-2 shadow-none d-flex align-items-center justify-content-between bg-white text-dark"
            style={{
              fontSize: '0.8125rem',
              cursor: (isLoading || isFetchingData) ? 'not-allowed' : 'pointer',
              minWidth: '120px',
              transition: 'all 0.2s ease-in-out',
              width: 'max-content',

              /* SOLUCIÓN AL BORDE: Forzamos la estructura explícita del borde con el color dinámico */
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: currentBorderColor,

              /* Efecto de anillo brillante del mismo color cuando el dropdown está abierto */
              boxShadow: isOpen ? `0 0 0 0.18rem ${currentBorderColor}33` : 'none'
            }}
        >
          <div className="d-flex align-items-center overflow-hidden flex-grow-1 me-2">
            {displayStateObj ? (
                <>
                  <div
                      className="rounded-circle me-2 flex-shrink-0"
                      style={{ width: '10px', height: '10px', backgroundColor: getStateColor(displayStateObj.type) }}
                  />
                  <span className="text-truncate fw-medium" style={{ color: getStateColor(displayStateObj.type) }}>
                    {displayStateObj.name}
                  </span>
                </>
            ) : (
                <span className="text-muted">Cargando...</span>
            )}
          </div>

          <div className="d-flex align-items-center flex-shrink-0 text-muted ps-1" style={{ minWidth: '20px', justifyContent: 'center' }}>
            {isLoading || isFetchingData ? (
                <div
                    className="spinner-border spinner-border-sm text-secondary opacity-75"
                    role="status"
                    style={{ width: '0.85rem', height: '0.85rem', borderColor: currentBorderColor, borderRightColor: 'transparent' }}
                />
            ) : (
                <i
                    className="ri-arrow-down-s-line fs-5"
                    style={{
                      transition: 'transform 0.2s ease',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: currentBorderColor
                    }}
                ></i>
            )}
          </div>

        </DropdownToggle>

        {/* ✂️ EL SEGUNDO SPINNER ABSOLUTO SE ELIMINÓ DE AQUÍ PARA EVITAR EL DUPLICADO */}

        <DropdownMenu className="shadow-sm border-0 py-1" style={{ minWidth: '100%', fontSize: '0.875rem', zIndex: 1050 }}>
          {displayStateObj && (
              <>
                <DropdownItem header className="py-1 text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>
                  Estado Actual
                </DropdownItem>
                <DropdownItem
                    className="bg-light text-dark fw-medium"
                    style={{ pointerEvents: 'none' }}
                >
                  <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle me-2"
                        style={{ width: '10px', height: '10px', backgroundColor: getStateColor(displayStateObj.type) }}
                    ></div>
                    {displayStateObj.name}
                    <i className="ri-check-line ms-auto text-success fs-5"></i>
                  </div>
                </DropdownItem>
              </>
          )}

          {allowedStates.length > 0 ? (
              <>
                <DropdownItem divider className="my-1" />
                <DropdownItem header className="py-1 text-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>
                  Cambiar a
                </DropdownItem>
                {allowedStates.map((state) => (
                    <DropdownItem
                        key={state.id}
                        onClick={() => handleSelectState(state.id)}
                        className="py-2"
                    >
                      <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle me-2"
                            style={{ width: '10px', height: '10px', backgroundColor: getStateColor(state.type) }}
                        ></div>
                        <span className="text-dark">{state.name}</span>
                      </div>
                    </DropdownItem>
                ))}
              </>
          ) : (
              <>
                <DropdownItem divider className="my-1" />
                <DropdownItem disabled className="py-2 text-center text-muted" style={{ fontSize: '0.8125rem' }}>
                  No hay transiciones<br/>disponibles
                </DropdownItem>
              </>
          )}
        </DropdownMenu>
      </Dropdown>
  );
};
